import { Alert, App as AntApp, Breadcrumb, Button, Card, Descriptions, Empty, Grid, Popconfirm, Skeleton, Space, Tag, Typography } from "antd";
import { CrownOutlined, DeleteOutlined, EditOutlined, PlusOutlined, UserAddOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import dayjs from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { GuardianDrawer } from "../components/GuardianDrawer.tsx";
import { AuditHistory } from "../components/AuditHistory.tsx";
import { AddStudentDrawer } from "../components/AddStudentDrawer.tsx";
import { ItemList } from "../components/ItemList.tsx";
import { TelegramLinkState } from "../components/TelegramLinkState.tsx";
import { useTelegramLinks } from "../app/telegram.ts";
import { FamilyBalanceCard } from "../components/FamilyBalanceCard.tsx";

type FamilyDetails = components["schemas"]["FamilyDetails"];
type GuardianDetails = components["schemas"]["GuardianDetails"];

type GuardianEditor = { kind: "closed" } | { kind: "add" } | { kind: "edit"; guardian: GuardianDetails };

export function FamilyDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { hasPermission } = useAuth();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const canManage = hasPermission("people.manage");
  const canReadAudit = hasPermission("audit.read");
  const canReadBalance = hasPermission("balance.read");
  const [guardianEditor, setGuardianEditor] = useState<GuardianEditor>({ kind: "closed" });
  const [addingStudent, setAddingStudent] = useState(false);
  const [editingName, setEditingName] = useState(false);

  const query = useQuery({
    queryKey: ["families", id],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/families/{id}", { params: { path: { id } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
  });
  const telegram = useTelegramLinks("Guardian", query.data?.guardians.map((g) => g.id) ?? []);

  /** Puts the fresh family into the cache and refreshes the list, so the page updates without a second round trip. */
  async function applyFamily(family: FamilyDetails) {
    queryClient.setQueryData(["families", id], family);
    await queryClient.invalidateQueries({ queryKey: ["families"], exact: false, refetchType: "inactive" });
    await queryClient.invalidateQueries({ queryKey: ["audit"] });
  }

  function reportError(err: unknown) {
    const lines = err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")];
    void message.error(lines.join(" "));
  }

  const rename = useMutation({
    mutationFn: async (name: string) => {
      const { data, error, response } = await api.PUT("/families/{id}", { params: { path: { id } }, body: { name } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (family) => {
      await applyFamily(family);
      setEditingName(false);
      void message.success(t("familyDetail.renamed"));
    },
    onError: reportError,
  });

  const makePayer = useMutation({
    mutationFn: async (guardianId: string) => {
      const { data, error, response } = await api.POST("/families/{id}/payer/{guardianId}", { params: { path: { id, guardianId } } });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (family) => {
      await applyFamily(family);
      void message.success(t("familyDetail.payerChanged"));
    },
    onError: reportError,
  });

  const removeGuardian = useMutation({
    mutationFn: async (guardianId: string) => {
      const { error, response } = await api.DELETE("/families/{id}/guardians/{guardianId}", { params: { path: { id, guardianId } } });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["families"] });
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
      void message.success(t("familyDetail.guardianRemoved"));
    },
    onError: reportError,
  });

  if (query.isPending) return <Skeleton active paragraph={{ rows: 8 }} />;
  if (query.isError || !query.data) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Alert
          type={notFound ? "warning" : "error"}
          showIcon
          title={notFound ? t("familyDetail.notFound") : t("app.error")}
          action={notFound ? undefined : <Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>}
        />
        <Link to="/families">{t("familyDetail.backToList")}</Link>
      </Space>
    );
  }

  const family = query.data;
  const payer = family.guardians.find((g) => g.isPayer);
  const canRemove = (g: GuardianDetails) => canManage && !g.isPayer && family.guardians.length > 1;
  const describeEntity = (entityId: string): string | undefined => {
    if (entityId === family.id) return family.name;
    const guardian = family.guardians.find((g) => g.id === entityId);
    if (guardian) return guardian.fullName;
    const student = family.students.find((s) => s.id === entityId);
    return student ? `${student.lastName} ${student.firstName}` : undefined;
  };
  const removeReason = (g: GuardianDetails) => (g.isPayer ? t("familyDetail.cannotRemovePayer") : t("familyDetail.cannotRemoveLast"));

  function guardianActions(guardian: GuardianDetails): React.ReactNode[] {
    const actions: React.ReactNode[] = [];
    if (!guardian.isPayer) {
      actions.push(
        <Button
          key="payer"
          type="link"
          icon={<CrownOutlined />}
          loading={makePayer.isPending && makePayer.variables === guardian.id}
          onClick={() => makePayer.mutate(guardian.id)}
        >
          {t("familyDetail.makePayer")}
        </Button>,
      );
    }
    actions.push(
      <Button key="edit" type="link" icon={<EditOutlined />} onClick={() => setGuardianEditor({ kind: "edit", guardian })}>
        {t("familyDetail.edit")}
      </Button>,
    );
    actions.push(
      canRemove(guardian) ? (
        <Popconfirm
          key="remove"
          title={t("familyDetail.removeGuardianConfirm", { name: guardian.fullName })}
          okText={t("familyDetail.remove")}
          cancelText={t("family.cancel")}
          okButtonProps={{ danger: true }}
          onConfirm={() => removeGuardian.mutate(guardian.id)}
        >
          <Button type="link" danger icon={<DeleteOutlined />} loading={removeGuardian.isPending && removeGuardian.variables === guardian.id}>
            {t("familyDetail.remove")}
          </Button>
        </Popconfirm>
      ) : (
        <Button key="remove" type="link" danger icon={<DeleteOutlined />} disabled title={removeReason(guardian)}>
          {t("familyDetail.remove")}
        </Button>
      ),
    );
    return actions;
  }

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Breadcrumb items={[{ title: <Link to="/families">{t("families.title")}</Link> }, { title: family.name }]} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title
          level={3}
          style={{ margin: 0 }}
          editable={
            canManage
              ? {
                  icon: <EditOutlined aria-label={t("familyDetail.rename")} />,
                  tooltip: t("familyDetail.rename"),
                  editing: editingName,
                  onStart: () => setEditingName(true),
                  onCancel: () => setEditingName(false),
                  onChange: (value) => {
                    const name = value.trim();
                    if (name.length === 0) {
                      void message.error(t("family.validation.required"));
                      return;
                    }
                    if (name === family.name) {
                      setEditingName(false);
                      return;
                    }
                    rename.mutate(name);
                  },
                  maxLength: 200,
                }
              : false
          }
        >
          {family.name}
        </Typography.Title>
        <Typography.Text type="secondary">{t("familyDetail.createdAt", { date: dayjs(family.createdAt).format("DD.MM.YYYY") })}</Typography.Text>
      </div>

      <Card
        title={t("family.guardians")}
        extra={
          canManage && (
            <Button icon={<UserAddOutlined />} onClick={() => setGuardianEditor({ kind: "add" })}>
              {t("family.addGuardian")}
            </Button>
          )
        }
      >
        <ItemList<GuardianDetails>
          items={family.guardians}
          itemKey={(guardian) => guardian.id}
          layout={screens.md ? "horizontal" : "vertical"}
          renderItem={(guardian) => ({
            ...(canManage ? { actions: guardianActions(guardian) } : {}),
            title: (
              <Space wrap>
                <span>{guardian.fullName}</span>
                <Tag>{t(`family.relationships.${guardian.relationship}`)}</Tag>
                {guardian.isPayer && (
                  <Tag color="gold" icon={<CrownOutlined />}>
                    {t("family.payer")}
                  </Tag>
                )}
              </Space>
            ),
            description: (
              <Descriptions size="small" column={1} colon={false} styles={{ label: { width: 150 } }}>
                <Descriptions.Item label={t("familyDetail.primaryPhone")}>
                  <a href={`tel:${guardian.phone}`}>{guardian.phone}</a>
                </Descriptions.Item>
                {guardian.additionalPhones.length > 0 && (
                  <Descriptions.Item label={t("familyDetail.additionalPhones")}>
                    <Space wrap>
                      {guardian.additionalPhones.map((phone) => (
                        <a key={phone} href={`tel:${phone}`}>
                          {phone}
                        </a>
                      ))}
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t("familyDetail.language")}>{t(`familyDetail.languages.${guardian.preferredLanguage}`, { defaultValue: guardian.preferredLanguage })}</Descriptions.Item>
                <Descriptions.Item label={t("telegram.label")}>
                  <TelegramLinkState
                    links={telegram.data?.filter((link) => link.profileId === guardian.id)}
                    loading={telegram.isPending}
                    failed={telegram.isError}
                    name={guardian.fullName}
                    canUnlink={canManage}
                  />
                </Descriptions.Item>
              </Descriptions>
            ),
          })}
        />
      </Card>

      <Card
        title={t("familyDetail.students")}
        extra={
          canManage && (
            <Button icon={<PlusOutlined />} onClick={() => setAddingStudent(true)}>
              {t("familyDetail.addStudent")}
            </Button>
          )
        }
      >
        {family.students.length === 0 ? (
          <Empty description={t("familyDetail.noStudents")} />
        ) : (
          <ItemList
            items={family.students}
            itemKey={(student) => student.id}
            renderItem={(student) => ({
              actions: [
                <Link key="open" to={`/students/${student.id}`}>
                  {t("familyDetail.openStudent")}
                </Link>,
              ],
              title: (
                <Space wrap>
                  <Link to={`/students/${student.id}`}>
                    {student.lastName} {student.firstName}
                  </Link>
                  <Tag color={student.status === "Active" ? "green" : "default"}>{t(`familyDetail.statuses.${student.status}`)}</Tag>
                </Space>
              ),
              description: student.dateOfBirth ? t("familyDetail.born", { date: dayjs(student.dateOfBirth).format("DD.MM.YYYY") }) : t("familyDetail.noDateOfBirth"),
            })}
          />
        )}
      </Card>

      {canReadBalance && <FamilyBalanceCard familyId={family.id} canManage={hasPermission("balance.manage")} />}

      {canReadAudit && (
        <Card title={t("audit.title")}>
          <AuditHistory entityType="Family" entityId={family.id} includeChildren describe={describeEntity} />
        </Card>
      )}

      <GuardianDrawer
        familyId={id}
        guardian={guardianEditor.kind === "edit" ? guardianEditor.guardian : null}
        open={guardianEditor.kind !== "closed"}
        onClose={() => setGuardianEditor({ kind: "closed" })}
        onSaved={(updated) => {
          const added = guardianEditor.kind === "add";
          setGuardianEditor({ kind: "closed" });
          void applyFamily(updated);
          void message.success(added ? t("familyDetail.guardianAdded") : t("familyDetail.guardianSaved"));
        }}
      />
      <AddStudentDrawer
        familyId={id}
        defaultLastName={payer?.fullName.split(" ").filter(Boolean).at(-1)}
        open={addingStudent}
        onClose={() => setAddingStudent(false)}
        onAdded={(student) => {
          setAddingStudent(false);
          void queryClient.invalidateQueries({ queryKey: ["families", id] });
          void queryClient.invalidateQueries({ queryKey: ["audit"] });
          void message.success(t("familyDetail.studentAdded", { name: `${student.firstName} ${student.lastName}`, count: student.guardians.length }));
        }}
      />
    </Space>
  );
}
